import React, {useCallback, useEffect, useState, useContext} from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Col,
  CustomerDialogButtonRow,
  H3,
  Row,
  SelectPure,
  CheckboxPure,
  WarningAlert,
  withInputContainer,
  ButtonLink,
} from '@modifi/ui'
import {
  Form,
  FormActionsContext,
  FormInput,
  SubmitBehavior,
  useInputValue,
  FormDataContext,
} from '@modifi/plugin-forms'
import {useTranslation} from '@modifi/plugin-i18n'
import {InferPropTypes} from '@modifi/typescript-utils'
import {useAbility} from '../../../../constants/permissions'
import {TRANSACTION} from '../../../../constants/section'
import {Description, StyledLabel, CompanyLabelWrapper, StyledSearchIcon} from './styles'
import SearchInput from '../../components/search-input'
import {DIALOG_NAME as DIALOG_PASS_THROUGH_TRANSACTION_SUCCESS} from '../update-transaction-success'

const SelectPureWithInputContainer = withInputContainer(SelectPure)

const propTypes = {
  params: PropTypes.shape({
    isPassThrough: PropTypes.bool.isRequired,
    id: PropTypes.number.isRequired,
  }).isRequired,
  markAsPassThroughIsPending: PropTypes.bool.isRequired,
  markAsPassThroughError: PropTypes.string,
  markAsPassThrough: PropTypes.func.isRequired,
  cleanupMarkAsPassThrough: PropTypes.func.isRequired,
  fetchTransaction: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  showDialog: PropTypes.func.isRequired,
}

const defaultProps = {
  markAsPassThroughError: null,
}

export type EditTransactionDialogProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const PassThroughTransactionDialog: React.FC<EditTransactionDialogProps> = ({
  params: {isPassThrough, id},
  handleClose,
  showDialog,
  markAsPassThroughIsPending,
  markAsPassThroughError,
  cleanupMarkAsPassThrough,
  markAsPassThrough,
  fetchTransaction,
}) => {
  const {t} = useTranslation('moneta')
  const [notifySeller, setNotifySeller] = useState(false)
  const [allowCompanyNameSearch, setAllowCompanyNameSearch] = useState(true)
  const [userEmails, setUserEmails] = useState([])
  const {onChange: onUserEmailChange} = useInputValue({name: 'userEmail'})
  const {data, changeData, clearData} = useContext(FormDataContext)
  useEffect(() => () => cleanupMarkAsPassThrough(), [cleanupMarkAsPassThrough])

  const ability = useAbility()
  const isAllowed = ability.can('create', TRANSACTION)

  useEffect(() => {
    if (userEmails && userEmails.length > 0) {
      onUserEmailChange(userEmails?.[0])
    }
  }, [onUserEmailChange, userEmails, changeData])

  const handleOnChange = useCallback((users: any) => {
    setUserEmails(users?.map((user: string) => ({value: user, label: user})))
    setAllowCompanyNameSearch(false)
  }, [])

  const handleNotifySeller = useCallback(
    (e: any) => {
      setNotifySeller(e?.target?.checked)
      if (!e?.target?.checked) {
        setUserEmails([])
        clearData()
        setAllowCompanyNameSearch(true)
      }
    },
    [clearData]
  )

  const handleSubmit = useCallback(
    async (evt: any) => {
      evt.preventDefault()
      try {
        await markAsPassThrough({
          transactionId: id,
          isPassThrough: !isPassThrough,
          sendPassThroughEmailToUser: data?.userEmail,
        })
      } catch (error) {
        // do nothing
        return false
      }
      fetchTransaction(id)
      handleClose()
      return showDialog(DIALOG_PASS_THROUGH_TRANSACTION_SUCCESS, {
        isPassThrough,
        sendPassThroughEmailToUser: data?.userEmail,
      })
    },
    [
      data?.userEmail,
      fetchTransaction,
      handleClose,
      id,
      isPassThrough,
      markAsPassThrough,
      showDialog,
    ]
  )

  const handleAllowSellerSearch = useCallback(() => {
    setAllowCompanyNameSearch(true)
    clearData()
  }, [clearData])

  return (
    <>
      {!isAllowed && (
        <Row mt={30} mb={10}>
          <Col col={10} offset={1}>
            <WarningAlert>{t('warning.alert.permission-denied')}</WarningAlert>
          </Col>
        </Row>
      )}
      <Row justifyContent="center">
        <Col auto>
          <H3 mt={40}>{t('pass-through-transaction.dialog.header')}</H3>
        </Col>
      </Row>

      <Row my={24}>
        <Col col={10} offset={1}>
          <Description>
            {t(
              `pass-through-transaction.dialog.${
                isPassThrough ? 'unmark' : 'mark'
              }-transaction.sub-header`
            )}
          </Description>
        </Col>
      </Row>
      <>
        {!isPassThrough && (
          <Row mb={24}>
            <Col col={8} offset={2}>
              <CheckboxPure
                name="notifySeller"
                label={t('pass-through-transaction.dialog.notify-seller.label')}
                checked={notifySeller}
                onChange={handleNotifySeller}
                value={notifySeller}
                data-ident="notify-seller"
              />
            </Col>
          </Row>
        )}
        {notifySeller && (
          <Row mb={24}>
            <Col col={8} offset={2}>
              <CompanyLabelWrapper>
                <StyledLabel>{t('pass-through-transaction.dialog.company-name.label')}</StyledLabel>
                {!allowCompanyNameSearch && (
                  <ButtonLink
                    small
                    icon
                    onClick={handleAllowSellerSearch}
                    data-ident="search-new-company"
                    data-target="search-new-company"
                  >
                    {t('pass-through-transaction.dialog.search-new-company.button.label')}
                    <StyledSearchIcon width={12} height={12} />
                  </ButtonLink>
                )}
              </CompanyLabelWrapper>
              <SearchInput
                onChange={handleOnChange}
                allowCompanyNameSearch={allowCompanyNameSearch}
              />
            </Col>
          </Row>
        )}
        {userEmails && userEmails.length > 0 && !allowCompanyNameSearch && (
          <Row>
            <Col col={8} offset={2}>
              <FormInput
                inputComponent={SelectPureWithInputContainer}
                name="userEmail"
                label={t('pass-through-transaction.dialog.user-email.label')}
                collapse
                options={userEmails}
              />
            </Col>
          </Row>
        )}
        {markAsPassThroughError && (
          <Row mt={30} mb={10}>
            <Col col={8} offset={2}>
              <Alert type="danger">{markAsPassThroughError}</Alert>
            </Col>
          </Row>
        )}
        <Row>
          <Col col={8} offset={2}>
            <FormActionsContext.Consumer>
              {ctx => (
                <CustomerDialogButtonRow
                  canSubmit={
                    (!notifySeller || ctx.hasChanges) &&
                    !ctx.validationPending &&
                    !markAsPassThroughIsPending &&
                    isAllowed
                  }
                  onSubmitClick={handleSubmit}
                  submitText={t('edit-transaction.dialog.submit.cta')}
                  hasCancel
                  cancelText={t('edit-transaction.dialog.cancel.cta')}
                  onCancelClick={handleClose}
                  loading={markAsPassThroughIsPending}
                  leftSpacing={0}
                  rightSpacing={0}
                />
              )}
            </FormActionsContext.Consumer>
          </Col>
        </Row>
      </>
    </>
  )
}

PassThroughTransactionDialog.propTypes = propTypes
PassThroughTransactionDialog.defaultProps = defaultProps

const PassThroughTransactionDialogWrapper = (props: any) => (
  <Form submitBehavior={SubmitBehavior.SUBMIT_DISABLED_UNLESS_VALID}>
    <PassThroughTransactionDialog {...props} />
  </Form>
)

export default PassThroughTransactionDialogWrapper
